import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@frontend-platform/ui-lib/ui';
import { FolderOpen } from 'lucide-react';
import { useMemo } from 'react';
import { IMPACT_BAND_ORDER, normalizeBand } from './band-config';
import { SuggestionCard } from './suggestion-card';
import type { RecommendationItem } from './types';

interface RecommendationsPanelProps {
  recommendations: RecommendationItem[];
}

// Default right-panel view (no criteria selected). Groups recommendations by
// impact band, ordered High → Medium → Low. Ported from Recommendations.vue.
export function RecommendationsPanel({ recommendations }: RecommendationsPanelProps) {
  const groups = useMemo(() => {
    const byBand = new Map<string, RecommendationItem[]>();
    for (const item of recommendations) {
      const band = normalizeBand(item.impactBand) || 'Low';
      const list = byBand.get(band) ?? [];
      list.push(item);
      byBand.set(band, list);
    }
    // Known bands first (in order), then any leftover bands.
    const ordered: { band: string; items: RecommendationItem[] }[] = [];
    for (const band of IMPACT_BAND_ORDER) {
      const items = byBand.get(band);
      if (items) ordered.push({ band, items });
    }
    for (const [band, items] of byBand) {
      if (!ordered.some((g) => g.band === band)) ordered.push({ band, items });
    }
    return ordered;
  }, [recommendations]);

  return (
    <div className="h-full overflow-auto rounded-lg border border-border bg-background">
      <div className="flex h-11 items-center justify-between border-border border-b px-6">
        <span className="font-semibold text-base text-foreground">Recommendations</span>
        {recommendations.length > 0 && (
          <span className="rounded bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs">
            Total {recommendations.length}
          </span>
        )}
      </div>

      {recommendations.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <FolderOpen className="h-5 w-5 text-muted-foreground" />
          <div className="font-medium text-foreground text-sm">No Recommendations</div>
          <div className="text-muted-foreground text-sm">
            No recommendations have been generated.
          </div>
        </div>
      ) : (
        <Accordion
          type="multiple"
          defaultValue={groups[0] ? [groups[0].band] : []}
          className="w-full"
        >
          {groups.map((group) => (
            <AccordionItem key={group.band} value={group.band} className="border-border border-b">
              <AccordionTrigger className="px-6 py-3 text-muted-foreground text-sm hover:no-underline">
                <span>
                  <span className="font-semibold">{group.band}</span>
                  <span className="ml-1 font-normal">({group.items.length})</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 px-6 pb-4">
                {group.items.map((item) => (
                  <SuggestionCard key={item.issueId} item={item} />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
